import { useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import api from "../api/client";
import ProjectNav from "../components/ProjectNav";

export default function SanityCheck() {
  const { projectId } = useParams();
  const { state } = useLocation() as any;
  const navigate = useNavigate();

  const [acknowledged, setAcknowledged] = useState(false);
  const [checksVisible, setChecksVisible] = useState(false);
  const [running, setRunning] = useState(false);
  const [sanityResult, setSanityResult] = useState<any>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [runError, setRunError] = useState("");

  // Version note character counter
  const [versionNote, setVersionNote] = useState("");

  // Basic Information
  const detectedPeriod =
    state?.evMeta?.validation?.detected_period ||
    state?.evMeta?.detected_quarter ||
    "";
  const [month, setMonth] = useState(detectedPeriod);
  const [dataAsOf, setDataAsOf] = useState(
    new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
  );
  const [reportTitle, setReportTitle] = useState(state?.reportTitle || (detectedPeriod ? `${detectedPeriod} M170 BRA EAC` : ""));

  const steps = ["Upload files", "Run Sanity Checks", "Configure", "Review & generate"];

  const runChecks = async () => {
    setRunning(true);
    setRunError("");
    setChecksVisible(false);
    setSanityResult(null);
    try {
      const res = await api.post("/sanity/check", {
        ev_tmp_path: state?.evMeta?.tmp_path || null,
        staffing_tmp_path: state?.staffingCentreMeta?.tmp_path || null,
        staffing_weekly_tmp_path: state?.staffingWeeklyMeta?.tmp_path || null,
        jira_wp_tmp_path: state?.jiraWpMeta?.tmp_path || null,
        jira_ro_tmp_path: state?.jiraRoMeta?.tmp_path || null,
        ims_tmp_path: state?.imsTmpPath || null,
        project_id: projectId || null,
      });
      setSanityResult(res.data);
      setRunId(res.data.run_id);
      setChecksVisible(true);
    } catch (err: any) {
      setRunError(err?.response?.data?.detail || "Sanity check failed. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  const downloadReport = async () => {
    if (!runId) return;
    try {
      const res = await api.get(`/sanity/check/${runId}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sanity_report_${runId.slice(0, 8)}.html`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  // Aggregate all warnings/errors across all file results + cross-file checks
  const allIssues: { text: string; severity: "warn" | "error" }[] = [];
  if (sanityResult) {
    for (const r of sanityResult.results || []) {
      for (const c of r.checks || []) {
        if (c.status === "warn" || c.status === "error") {
          allIssues.push({
            text: `[${r.file_type || r.file_name}] ${c.name}${c.detail ? " — " + c.detail : ""}`,
            severity: c.status,
          });
        }
      }
    }
    for (const c of sanityResult.cross_file_checks || []) {
      if (c.status === "warn" || c.status === "error") {
        allIssues.push({
          text: `[Cross-file] ${c.name}${c.detail ? " — " + c.detail : ""}`,
          severity: c.status,
        });
      }
    }
  }

  const CheckItem = ({ check }: { check: any }) => {
    const isError = check.status === "error";
    const isWarn = check.status === "warn";
    return (
      <div className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0">
        {check.status === "pass" ? (
          <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : isError ? (
          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )}
        <div>
          <p className={`text-xs font-medium ${check.status === "pass" ? "text-green-700" : isError ? "text-red-700" : "text-amber-700"}`}>
            {check.name}
          </p>
          {check.detail && <p className="text-xs text-gray-400 mt-0.5">{check.detail}</p>}
        </div>
      </div>
    );
  };

  const FileCard = ({ result }: { result: any }) => {
    const hasError = result.error_count > 0;
    const hasWarn = result.warning_count > 0;
    const [expanded, setExpanded] = useState(true);

    const statusBadge = hasError ? (
      <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded font-medium">
        {result.error_count} ERROR{result.error_count !== 1 ? "S" : ""}
      </span>
    ) : hasWarn ? (
      <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded font-medium">
        {result.warning_count} WARNING{result.warning_count !== 1 ? "S" : ""}
      </span>
    ) : (
      <span className="text-xs bg-green-50 text-green-600 border border-green-200 px-2 py-0.5 rounded font-medium">
        PASS
      </span>
    );

    return (
      <div className={`bg-white rounded-xl border p-5 ${hasError ? "border-red-200" : hasWarn ? "border-amber-200" : "border-gray-200"}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm font-semibold text-gray-900">{result.file_type || result.file_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge}
            <button onClick={() => setExpanded(e => !e)} className="text-gray-400 hover:text-gray-600">
              <svg className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-400">Validation complete</p>
          <p className="text-xs font-medium text-blue-600">100%</p>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4">
          <div className={`h-1.5 rounded-full ${hasError ? "bg-red-500" : hasWarn ? "bg-amber-500" : "bg-green-500"}`} style={{ width: "100%" }}></div>
        </div>

        {result.file_name && (
          <p className="text-xs text-gray-400 mb-2">
            {result.file_name}
            {result.file_size_mb != null ? ` · ${result.file_size_mb} MB` : ""}
            {result.row_count != null ? ` · ${result.row_count} rows` : ""}
            {result.detected_period ? ` · Period: ${result.detected_period}` : ""}
          </p>
        )}

        {expanded && (
          <div className="max-h-44 overflow-y-auto pr-1">
            {(result.checks || []).map((c: any, i: number) => <CheckItem key={i} check={c} />)}
          </div>
        )}
      </div>
    );
  };

  const SkeletonCard = ({ title }: { title: string }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-40"></div>
      </div>
      <div className="flex items-center justify-between mb-1">
        <div className="h-3 bg-gray-100 rounded w-24"></div>
        <div className="h-3 bg-blue-100 rounded w-8"></div>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4 overflow-hidden">
        <div className="h-1.5 bg-blue-400 rounded-full animate-[shimmer_1.5s_infinite]" style={{ width: "60%" }}></div>
      </div>
      <p className="text-xs text-blue-500">Running checks on {title}...</p>
      <div className="space-y-2 mt-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-3 bg-gray-100 rounded w-full"></div>
        ))}
      </div>
    </div>
  );

  const fileLabels = [
    { key: "evMeta", label: "EVM Data File" },
    { key: "staffingCentreMeta", label: "Staffing Centre" },
    { key: "staffingWeeklyMeta", label: "Staffing Weekly" },
    { key: "jiraWpMeta", label: "Jira Work Packages" },
    { key: "jiraRoMeta", label: "Jira R&O" },
  ];
  const skeletonFiles = fileLabels.filter(f => state?.[f.key]?.tmp_path);

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="report" />

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SANITY CHECK</h1>
            <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">
              We're verifying file integrity, schema, and data quality.
            </p>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input placeholder="Search programs by name or code..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none w-64" />
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-0 mb-6 mt-6">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                  ${i === 0 ? "bg-green-500 text-white"
                    : i === 1 ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"}`}>
                  {i === 0 ? "✓" : i + 1}
                </div>
                <span className={`text-sm
                  ${i === 0 ? "text-green-600"
                    : i === 1 ? "text-blue-600 font-medium"
                    : "text-gray-400"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className="w-8 h-px bg-gray-300 mx-3"></div>}
            </div>
          ))}
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <p className="text-sm font-semibold text-gray-800 mb-4">Basic Information</p>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">What month is this report for?</label>
              <input
                value={month}
                onChange={e => setMonth(e.target.value)}
                placeholder="e.g. 2026-01"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {detectedPeriod && (
                <p className="text-xs text-gray-400 mt-1">Detected from data: {detectedPeriod}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Data as of date?</label>
              <div className="relative">
                <input
                  type="text"
                  value={dataAsOf}
                  onChange={e => setDataAsOf(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-9 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-gray-500">Report title / name?</label>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">AUTO-GENERATED</span>
              </div>
              <div className="relative">
                <input
                  value={reportTitle}
                  onChange={e => setReportTitle(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-9 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <p className="text-xs text-gray-400 mt-1">Report title editable</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-gray-500">Add Version Note</label>
              <span className="text-xs text-gray-400">OPTIONAL</span>
            </div>
            <textarea
              maxLength={1000}
              value={versionNote}
              onChange={e => setVersionNote(e.target.value)}
              placeholder="Paste or type customer feedback here. The AI Agent will reference this when generating narratives, risk summaries and recommendations in the report..."
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{versionNote.length}/1000</p>
          </div>

          <div className="flex justify-between items-center mt-5 pt-4 border-t border-gray-100">
            <button onClick={() => navigate(`/upload/${projectId}`)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <button
              onClick={runChecks}
              disabled={running}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2">
              {running && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {running ? "Running checks..." : "Run sanity check"}
            </button>
          </div>
          {runError && <p className="text-red-500 text-xs mt-2 text-right">{runError}</p>}
        </div>

        {/* Skeleton loading cards */}
        {running && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            {skeletonFiles.map(f => <SkeletonCard key={f.key} title={f.label} />)}
          </div>
        )}

        {/* Real results */}
        {checksVisible && sanityResult && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {sanityResult.results.map((r: any, i: number) => (
                <FileCard key={i} result={r} />
              ))}
            </div>

            {/* Cross-file checks */}
            {sanityResult.cross_file_checks?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-3">Cross-file Checks</p>
                {sanityResult.cross_file_checks.map((c: any, i: number) => (
                  <CheckItem key={i} check={c} />
                ))}
              </div>
            )}

            {/* Summary stats */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{sanityResult.summary?.files_checked ?? 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">Files checked</p>
              </div>
              <div className="flex-1 bg-white rounded-xl border border-red-100 p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{sanityResult.summary?.total_errors ?? 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">Errors</p>
              </div>
              <div className="flex-1 bg-white rounded-xl border border-amber-100 p-4 text-center">
                <p className="text-2xl font-bold text-amber-500">{sanityResult.summary?.total_warnings ?? 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">Warnings</p>
              </div>
              <div className="flex-1 bg-white rounded-xl border border-green-100 p-4 text-center">
                <p className={`text-2xl font-bold ${sanityResult.summary?.overall_status === "pass" ? "text-green-600" : sanityResult.summary?.overall_status === "warn" ? "text-amber-500" : "text-red-600"}`}>
                  {(sanityResult.summary?.overall_status || "—").toUpperCase()}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Overall status</p>
              </div>
            </div>

            {/* Warnings/Errors summary box */}
            {allIssues.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs font-semibold text-amber-800">
                    {allIssues.length} issue{allIssues.length !== 1 ? "s" : ""} found — review before proceeding
                  </p>
                </div>
                <div className="space-y-1 mb-2 max-h-32 overflow-y-auto pr-1">
                  {allIssues.map((w, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      {w.severity === "error" ? (
                        <svg className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      )}
                      <p className={`text-xs leading-tight ${w.severity === "error" ? "text-red-700" : "text-amber-700"}`}>{w.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
                  <input
                    type="checkbox"
                    id="ack"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-amber-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="ack" className="text-xs text-amber-800 cursor-pointer font-medium">
                    I acknowledge these issues and want to proceed
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bottom buttons */}
        <div className="flex justify-between mt-4">
          <div className="flex gap-2">
            <button onClick={() => navigate(`/upload/${projectId}`)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <button
              onClick={downloadReport}
              disabled={!runId}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download validation report
            </button>
          </div>
          <button
            onClick={() => navigate(`/configure/${projectId}`, {
              state: { ...state, month, dataAsOf, reportTitle, versionNote, sanityResult }
            })}
            disabled={!checksVisible || (!acknowledged && allIssues.length > 0)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            Configure report →
          </button>
        </div>
      </div>
    </div>
  );
}
